import {
  UNIT_BADGES as RAW_UNIT_BADGES,
  unitBadgeLabel as rawUnitBadgeLabel,
  worstBadgeSeverity as rawWorstBadgeSeverity,
} from '@platform/shared-types';
import type { UnitBadge, UnitBadgeSeverity } from '@platform/shared-types';

/**
 * GUARDED ACCESS TO THE BADGE CATALOGUE.
 *
 * `@platform/shared-types` is CommonJS and both apps pre-bundle it on purpose,
 * but Vite keys that cache on the package version — which has been 2.0.0
 * forever — so it NEVER re-bundles on its own. A pre-bundle built before these
 * exports existed hands back `undefined`, and calling one at module scope
 * white-screens the entire page rather than failing where it is used. That is
 * exactly how `worstBadgeSeverity is not a function` took down Receiving.
 *
 * Every runtime import is guarded here, not only the symbol that happened to
 * crash first: fixing one and leaving its neighbours just moves the crash a few
 * lines down. Type-only imports are erased at build and need no guard.
 *
 * Each fallback degrades to something harmless — an empty catalogue, the raw
 * id, no severity — so a stale cache costs badge labels and colour, never the
 * screen.
 */

const STALE = (symbol: string) => {
  console.error(
    `@platform/shared-types exported no ${symbol} — a stale Vite pre-bundle. `
    + 'RESTART the dev server (npm run dev -- --force): deleting node_modules/.vite '
    + 'under a running server does nothing, because Vite holds the optimised bundle '
    + 'in memory and pins its hash into every module URL the browser already has.',
  );
};

/** The catalogue, or an empty list when the pre-bundle predates it. */
export const UNIT_BADGES: readonly UnitBadge[] = (() => {
  if (Array.isArray(RAW_UNIT_BADGES)) return RAW_UNIT_BADGES;
  STALE('UNIT_BADGES');
  return [];
})();

/** A badge's label, degrading to the raw id — which is still identifiable. */
export const unitBadgeLabel: (id: string) => string =
  typeof rawUnitBadgeLabel === 'function'
    ? rawUnitBadgeLabel
    : (id: string) => {
      STALE('unitBadgeLabel');
      return id;
    };

/** The worst severity present, degrading to undefined — colour is lost, not the row. */
export const worstBadgeSeverity: (ids: readonly string[]) => UnitBadgeSeverity | undefined =
  typeof rawWorstBadgeSeverity === 'function'
    ? rawWorstBadgeSeverity
    : () => {
      STALE('worstBadgeSeverity');
      return undefined;
    };
